import i18n from 'i18next';
import { initReactI18next } from 'react-i18next';

const baseDictionary = {
  en: {
    translation: {
      navigation: {
        dashboards: 'Dashboards',
        strategy: 'Strategy',
        financials: 'Financials',
        process: 'Process',
        process_analysis: 'Process Analysis',
        adherence: 'Adherence',
        integrations: 'Integrations',
        automation: 'Automation',
        work_periods: 'Work Periods',
      },
      common: {
        // Common terms used across multiple modules
        active: 'Active',
        inactive: 'Inactive',
        current: 'Current',
        calibrated: 'Calibrated',
        work_units: 'Tasks',
        work_unit: 'Task',
        parent_task: 'Epic',
        estimation: 'Points',
        estimation_singular: 'Point',
        work_unit_description: 'Description',
        description: 'Description',
        priority: 'Priority',
        assignee: 'Assignee',
        reporter: 'Reporter',
        component: 'Component',
        components_plural: 'Components',
        label: 'Label',
        labels_plural: 'Labels',
        time_original_estimate: 'Time Original Estimate',
        work_unit_details: 'Work Unit Details',
        name: 'Name',
        type: 'Type',
        created: 'Created',
        completed: 'Completed',
        status_history: 'Status History',
        initiative: 'Initiative',
        initiatives: 'Initiatives',
        bug: 'Bug',
        debt: 'Debt',
        epic: 'Epic',
        epics: 'Epics',
        teams: 'Teams',
        filters: 'Filters',
        sprint: 'Sprint',
        sprints: 'Sprints',
        last_updated: 'Last Updated',
        portfolio: 'Portfolio',
        portfolios: 'Portfolios',
        project: 'Project',
        projects: 'Projects',
        board: 'Board',
        boards: 'Boards',
        work_periods: 'Work Periods',
        assigned_boards: 'Assigned Boards',
        no_boards_selected: 'No boards selected',
        related_tasks: 'Related {{work_units}}',
        settings: 'Settings',
        notification: 'Notification',
        integrations: {
          teams_select: 'Below Teams, select the team you would like to integrate.',
        },
        upload: {
          xlsx: 'Upload XLSX File',
        },
        actions: {
          add: 'Add',
          add_new: 'Add New',
          back: 'Back',
          edit: 'Edit',
          delete: 'Delete',
          download: 'Download',
          save: 'Save',
          search: 'Search',
          cancel: 'Cancel',
          create: 'Create',
          reset: 'Reset',
          export: 'Export',
          close: 'Close',
        },
        status: {
          status: 'Status',
          statuses: 'Statuses',
          done: 'Done',
          in_progress: 'In Progress',
          blocked: 'Blocked',
        },
        ai: {
          assistant: 'AI Assistant',
          chat_history: 'Chat History',
        },
        dashboard: {
          dashboard: 'Dashboard',
          dashboards: 'Dashboards',
          template: 'Template',
          templates: 'Templates',
          data_entry: 'Data Entry',
          widget: 'Widget',
          widgets: 'Widgets',
          loading: 'Loading...',
          error_loading: 'Error loading data',
          create_widget_help: 'Easily create a new widget to tailor your dashboard to your needs.',
          create_title: 'Create New Dashboard',
          name_label: 'Dashboard Name',
          name_placeholder: 'Costs vs. Output Dashboard',
          delete_initiative: 'Delete Initiative',
          delete_initiative_message:
            'This will delete the {name} initiative from Bloomfilter. It does not impact any integrated source systems.',
          continue: 'Continue',
          cancel: 'Cancel',
          description: 'Description',
          description_placeholder:
            'Measures the average time taken by the QA team to respond to assigned work units or issues.',
          data_pool: 'Data Pool',
          data_pool_placeholder: 'Purple Portfolio',
          create_button: 'Create Dashboard',
        },
        measures: 'Measures',
        form: {
          email: 'Email',
          name: 'Name',
          password: 'Password',
          confirm_password: 'Confirm Password',
          create_account_form: 'Create Account Form',
          login_form: 'Login Form',
          forgot_password_form: 'Forgot Password Form',
          forgot_password: 'Forgot Your Password?',
          reset_password: 'Reset Password',
          remember_password: 'Remember your password?',
          login_to_bloomfilter: 'Log in to Bloomfilter',
          reset_help: "Don't worry! We'll help you reset it quickly.",
          enter_email: 'Enter the email associated with your Bloomfilter account.',
          create_organization: 'Create Organization',
          organization_name: 'Organization Name',
          organization_description:
            'Organizations are spaces where you can access and view data from your integrations.',
        },
        placeholders: {
          select_sprint: 'Select a Sprint',
          email: 'Enter your email address',
          name: 'Enter your full name',
          password: 'Enter your password',
          confirm_password: 'Confirm your password',
          organization: 'Enter your organization',
          select_category: 'Select category',
          select_team: 'Select Team',
          select_measure: 'Select measure',
          select_workflow: 'Select a workflow',
          select_date: 'Select date',
          pick_dates: 'Pick dates range',
          pick_year: 'Pick year',
        },
        currency: {
          dollar_value: '${{value}}',
        },
        empty_filter: {
          no_objects_found: 'No {{objectName}} found.',
          try_different_filter: 'Please try a different filter.',
        },
      },
      process_analysis: {
        workload_distribution: {
          capacity_work_units: 'Capacity ({{work_units}})',
          capacity_estimation: 'Capacity ({{estimation}})',
          workload_distribution: 'Workload Distribution',
          tooltips: {
            capacity: 'Total number of completed {{work_units}} over the time period',
            estimation: 'Total {{estimation}} completed over the time period',
            distribution: 'Distribution of work across teams',
          },
        },
        assessment: {
          phase: {
            ready: 'Ready',
            blocked: 'Blocked',
            wip: 'WIP',
            review: 'In Review',
            test: 'In Test',
            deployable: 'To Deploy',
            done: 'Done',
          },
          pace: {
            title: 'Work pace explorer',
          },
          burnt_title: 'Burnt',
          burnt_help: '% of work period elapsed',
          done_help: 'work finished',
          work_unit_process_map: 'Work Unit Process Map',
          considered_work_units: 'Considered {{work_units}}',
          scope_selected: 'for the scope selected',
          this_sprint: 'This {{sprint}}',
          goals: 'Goals',
          goals_for_this_sprint: '{{goals}} for this {{sprint}}',
          key_measures: {
            strategy: 'Strategy',
            complexity: 'Complexity',
            quality: 'Quality',
            scope_creep: 'Scope',
            readiness: 'Readiness',
            independence: 'Independence',
            review_time: 'Review Time',
            declined_change_requests: 'PR closed (%)',
          },
        },
        initiative_completion: {
          title: 'Initiative Completion',
          initiatives: 'Initiatives',
          line: 'Line',
          trend: 'Trend',
        },
        initiative_focus: {
          title: 'Initiative Focus',
          initiatives: 'Initiatives',
          line: 'Line',
          trend: 'Trend',
        },
        key_measures: {
          readiness_label:
            'The number of work units which have a name, description, and {{estimation}}. Note: {{estimation}} intentionally set to 0 are considered estimated.',
          complexity_label: 'The average {{estimation}} of the work units in the scope selected.',
        },
        scope: {
          title: 'Scope',
          legend: 'Legend',
        },
      },
      strategy: {
        initiatives: {
          throughput: 'Throughput',
          work_progress: 'Work Progress',
          strategic_progress: 'Strategic Progress',
          add_new: 'Add New',
          create: 'Create Initiative',
          edit: 'Edit Initiative',
          no_initiatives: 'No initiatives have been created yet',
          no_epics: 'No Epics',
          no_end_date: 'No End Date',
          step_indicator: '(Step {{step}}/2)',
          unsaved_changes: 'You may have unsaved changes. Are you sure you want to close without saving?',
          created_success: '{{name}} has been created successfully.',
          updated_success: '{{name}} has been successfully updated.',
          deleted_success: '{{name}} has been successfully removed.',
          create_error: 'Failed to create initiative: {{error}}',
          unknown_error: 'Unknown error occurred',
          table_view: 'Table View',
          tree_map_view: 'Tree Map View',
          metric: 'Metric:',
          search_placeholder: 'Search for initiatives',
          export: 'Export',
          create_presentation: 'Create Presentation',
          back: 'Back',
          initiatives_title: 'Initiatives',
          edit_button: 'Edit Initiative',
          no_description: 'No description available.',
          more_initiative_details: 'More Initiative Details',
          other: 'Other',
          combined_initiatives: 'Combined Low-Value Initiatives',
          statuses: {
            at_risk: 'At Risk',
            off_track: 'Off Track',
            on_track: 'On Track',
            created: 'Created',
            not_active: 'Not Active',
            not_started: 'Not Started',
            unknown: 'Unknown',
          },
          validation: {
            start_date_before_end_date: 'Start date must be before end date',
            end_date_future: 'End date must be in the future',
            end_date_after_start_date: 'End date must be after start date',
          },
          presentation: {
            title: 'Create Google Slides Presentation',
            auth_required:
              'You need to authorize Google access first. Please click the button below to connect your Google account.',
            name_label: 'Presentation Name',
            name_placeholder: 'Enter presentation name',
            will_create: 'This will create a Google Slides presentation with {{count}} initiative',
            will_create_plural: 'This will create a Google Slides presentation with {{count}} initiatives',
            connect_google: 'Connect Google Account',
            created_success: 'Presentation "{{name}}" created successfully. Click OK to open it.',
            creation_error: 'Error: {{error}}',
          },
          form: {
            description: 'Initiatives represent broad strategic goals and objectives for your organization.',
            name_label: 'Name',
            name_placeholder: 'Enter initiative name here',
            description_label: 'Description',
            description_placeholder: 'Enter initiative description here',
            start_date_label: 'Start Date',
            end_date_label: 'End Date',
            date_placeholder: 'Pick date',
            business_value_label: 'Relative Business Value',
            business_value_placeholder: 'Enter relative business value here',
            numeric_label: 'Numeric',
            dollar_label: 'Dollar',
            delete_button: 'Delete Initiative',
            next_step: 'Next Step',
            back: 'Back',
            cancel: 'Cancel',
            save_changes: 'Save Changes',
            complete_and_create: 'Complete and Create',
            epic_description:
              'Epics contribute to achieving Initiatives. Epics are groups of work consisting of many tasks.',
            select_epics: 'Select Epics',
            search_and_select_epics: 'Search and select epics',
          },
          table: {
            id: 'ID',
            initiative_id: 'Initiative ID',
            name: 'Name',
            completion: 'Completion',
            target_end: 'Target End',
            cost_to_date: 'Cost to Date',
            teams: 'Teams',
            empty: 'Empty',
            tasks: 'Tasks',
          },
          metrics: {
            tasks: 'Tasks',
            points: 'Points',
            units: 'Units',
            cost_by_initiative: 'Cost by Initiative',
            effort_by_initiative: 'Effort by Initiative',
          },
          labels: {
            same_as_start_date: 'Same as Start Date',
            same_as_end_date: 'Same as End Date',
            status_by_points: 'Status (By Points)',
            status_by_tasks: 'Status (By Tasks)',
            no_data: '-',
            hide_empty_epics: 'Hide empty epics',
          },
          delete: {
            title: 'Delete Initiative',
            description_first: 'This will delete the <strong>{{name}}</strong>.',
            description_second:
              'This action will delete the initiative from Bloomfilter. It aligns with the major project areas, but will not affect any integrated source systems.',
            confirm: 'Confirm and Delete',
          },
        },
        overview: {
          title: 'KPIs Overview',
          by_status: 'Initiatives by Statuses',
          by_state: 'Initiatives by State',
          completion: 'Overall Initiatives Completion',
          portfolio_throughput: 'Portfolio Throughput',
          tasks_completed: 'Tasks Completed',
          last_30_days: 'Last 30 Days',
        },
        filters: {
          no_filters_applied: 'No filters selected. Choose filters to see results',
          status: 'Status',
          all_statuses: 'All Statuses',
          state: 'State',
          activity: 'Activity',
          active_inactive: 'Active / Inactive',
          source: 'Source',
          all_sources: 'All Sources',
          all_epics: 'All Epics',
          no_epics_found: 'No epics found',
          all_teams: 'All Teams',
          types: 'Types',
          all_types: 'All Types',
          no_teams_found: 'No teams found',
          search_by_epic: 'Search by Epic',
          search_by_team: 'Search by Team',
          completed_only: 'Completed Only',
        },
        initiative_performance: {
          no_data: 'No data available',
          no_filtered_data: 'No data available for the selected filters',
          add_target: 'Add a target to start tracking your initiative performance',
          throughput: 'Throughput',
          completion: 'Completion',
          focus: 'Focus',
          flow_by_phase: 'Flow By Phase',
          targets: 'Targets',
          create_target: 'Create Target',
          cost: 'Cost',
          cost_by_units: 'Cost By {{units}}',
          related_work_units: 'Related Work Units',
          search_placeholder: 'Search by task name or ID',
          export: 'Export',
          pick_date: 'Pick date',
          initiative_completion: 'Initiative Completion',
          time_elapsed: 'Time Elapsed',
          cost_to_date: 'Cost to Date',
          start_date: 'Start Date',
          end_date: 'End Date',
          activity_start_date: 'Activity Start Date',
          activity_end_date: 'Activity End Date',
          details_title: 'Details',
          max_selection_limit: 'You can select up to {{count}} items at a time',
          selected_date: 'Selected Date',
          completed_tasks: 'Completed Tasks',
          team_details: 'Team Details',
          performance_score_current: 'Performance Score (Current)',
          project_manager: 'PM',
          cost_to_date_initiative: 'Cost to Date (This Initiative)',
          start_date_first_task: 'Start Date (First Task)',
          task_count_initiative: 'Task Count (This Initiative)',
          task_log: 'Task Log',
          status_changed_to: 'Status changed to',
          changed_by: 'by {{user}}',
          labels: {
            initiative_progress: 'Initiative Progress',
            contributing_tasks: 'Contributing Work Units',
            contributing_epics: 'Contributing Epics',
            contributing_teams: 'Contributing Teams',
            contributing_boards: 'Contributing Boards',
          },
          related_tasks: {
            title: 'Related Work Units',
            table: {
              id: 'ID',
              name: 'Name',
              epic: 'Epic',
              points: 'Points',
              type: 'Type',
              created: 'Created',
            },
          },
          phases: {
            ready: 'Ready',
            backlog: 'Backlog',
            blocked: 'Blocked',
            wip: 'WIP',
            in_review: 'In Review',
            in_test: 'In Test',
            to_deploy: 'To Deploy',
            done: 'Done',
          },
          chart_labels: {
            focus: 'Focus',
            total_tasks: 'Total Tasks',
            total_points: 'Total Points',
            completed_tasks: 'Completed Tasks',
            completed_points: 'Completed Points',
            future_total_tasks: 'Total Tasks (Future Projection)',
            future_total_points: 'Total Points (Future Projection)',
            future_completed_tasks: 'Completed Tasks (Future Projection)',
            future_completed_points: 'Completed Points (Future Projection)',
            per_month: 'per month',
          },
        },
      },
      process: {
        section_graph: {
          title: '{{work_units}} Process Map',
          legend: {
            expected_steps: 'Expected Steps',
            skipped_steps: 'Skipped Steps',
            backwards_steps: 'Backwards Steps',
            blocked_steps: 'Blocked (Unexpected)',
          },
        },
        scope: {
          task_type: '{{work_units}} Type',
          task_type_placeholder: 'Select a {{work_units}} type',
        },
        map: {
          considered_work_units: 'Considered {{work_units}}',
          scope_selected: 'for the scope selected',
          explanation: {
            what_title: 'What does this map do?',
            what_text:
              'The map details the number of work units (stories, work units, bugs, subtasks) that were completed for the selected time period and filters.',
            how_title: 'How is the map drawn?',
            how_text: 'Each major box matches the project status that was mapped to Bloomfilter during onboarding.',
            process_flow:
              'Lines connect each step vertically to complete the expected path. The thickness of each line visualizes the volume of completed work units that travelled that part of the process flow for the time period selected.',
            status_note:
              'Work unit transitions can cause discrepancy from their source system when one or more customer statuses are used on a work unit in a desired time period.',
            future_note:
              'In future iterations, Bloomfilter will make the Process Map available to the board/filter level, but at this time it is only available to analyze the Portfolio and Team (project) levels.',
          },
        },
        event_graph: {
          event: 'Event',
          event_plural: 'Events',
        },
      },
      financials: {
        details: '{{work_units}} Details',
        cost_per_task: 'Cost per {{work_units}}',
        cost: 'Cost',
        daily_spend: 'Daily Spend',
        tasks_committed: '{{work_units}} Committed',
        points_committed: '{{estimation}} Committed',
        velocity: 'Velocity',
        throughput: 'Throughput',
        tasks_completed: '{{work_units}} Completed',
        points_completed: '{{estimation}} Completed',
        cost_per_point: 'Cost per {{estimation_singular}}',
        related_work_units: 'Related {{work_units}}',
        no_work_units: 'There are no related {{work_units}} for this selection.',
        tooltips: {
          capacity: 'Total number of completed {{work_units}} over the time period',
          estimation: 'Total {{estimation}} completed over the time period',
          distribution: 'Distribution of work across teams',
        },
      },
      initiatives: {
        section_status: {
          no_end_date: 'The end date is generated because no end date was provided',
        },
        initiative_performance: {
          section_status: {
            cumulative_for_initiative: 'cumulative for {{initiative}}',
            total_tasks: 'Total {{metric}}',
            completed_tasks: 'Completed {{metric}}',
          },
        },
      },
      data_management: {
        data_management: 'Data Management',
        workflows: {
          workflows: 'Workflows',
          workflow: 'Workflow',
          assigned_boards: 'Assigned {{boards}}',
          project_board_name: '{{project}} / {{board}} Name',
          active_inactive_statuses: '{{active}} / {{inactive}} {{statuses}}',
          no_boards_selected: 'No {{boards}} selected',
          add_new_workflow: 'Add New Workflow',
          workflow_map: 'Workflow Map',
          mapped: 'Mapped',
          unmapped: 'Unmapped',
          search_and_add_boards: '{{search}} and {{add}} {{boards}}',
          active_statuses: '{{active}} {{statuses}}',
          add_boards_to_view_statuses: 'Add {{boards}} to view {{statuses}} here',
        },
        data_sources: {
          data_sources: 'Data Sources',
        },
        board_settings: {
          board_settings: '{{board}} {{settings}}',
        },
      },
      work_periods: {
        burn_down: 'Work Completion',
        burn_up: 'Goal Attainment',
      },
      targets: {
        thresholds: 'Thresholds',
        threshold: 'Threshold',
        green: 'Green',
        yellow: 'Yellow',
        red: 'Red',
      },
    },
  },
};

i18n.use(initReactI18next).init({
  resources: baseDictionary,
  lng: 'en',
  interpolation: {
    escapeValue: false,
  },
  react: {
    bindI18n: 'loaded languageChanged',
    bindI18nStore: 'added',
    useSuspense: true,
  },
});

export default i18n;
